import React, { useState } from "react";
import SEO from "../../../components/SEO";
import img30 from "../../../assets/img30.jpg";
import img10 from "../../../assets/img10.jpg";
import { CheckCircle, Plus, Minus } from "lucide-react";
import BlogSection from "../../../components/BlogSection";

export default function HealthInsurance() {
  const container = "max-w-[1380px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12";
  const [activeIndex, setActiveIndex] = useState(null);
  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "Qu'est-ce que l'assurance maladie au Canada?",
      answer:
        "L'assurance maladie au Canada offre une couverture pour les services non entièrement payés par les régimes provinciaux, incluant les soins dentaires, les soins de la vue, les médicaments d'ordonnance et les soins paramédicaux. Elle aide les particuliers et les familles à accéder aux soins de santé essentiels sans coûts imprévus. Détenir une assurance maladie privée assure une protection financière, une tranquillité d'esprit et la capacité de recevoir des services médicaux en temps opportun. Cette couverture complète judicieusement le régime de santé provincial, facilitant la gestion des dépenses de santé et le maintien du bien-être à long terme pour vous et votre famille.",
    },
    {
      question: "Comment montrer une preuve temporaire d'assurance maladie au Canada?",
      answer:
        "Pour montrer une preuve temporaire d'assurance maladie au Canada, vous pouvez utiliser des documents comme votre carte d'assurance, la page de déclaration de la police, ou une lettre de votre assureur confirmant une couverture active. Les visiteurs et les nouveaux résidents reçoivent souvent des documents de couverture temporaire de la part d'assureurs privés ou des autorités provinciales de santé en attendant leur pleine admissibilité. Ayez toujours ces documents sur vous lorsque vous consultez, car les cliniques et hôpitaux peuvent demander une preuve de couverture avant de fournir des services.",
    },
    {
      question: "Puis-je ajouter un parent à mon assurance maladie au Canada?",
      answer:
        "Oui, plusieurs régimes d'assurance maladie privée au Canada vous permettent d'ajouter un parent comme personne à charge. Cela leur procure une couverture pour les frais médicaux, les médicaments d'ordonnance et d'autres besoins de santé, leur assurant de recevoir les soins nécessaires sans stress financier. Ajouter un parent aide les familles à protéger leurs proches et à gérer efficacement les coûts de santé, offrant la tranquillité d'esprit de savoir que les membres de votre famille sont couverts par un seul régime complet.",
    },
    {
      question: "Comment fonctionne l'assurance maladie au Canada?",
      answer:
        "Au Canada, l'assurance maladie combine une couverture publique et privée. Les régimes provinciaux offrent des soins de santé de base aux résidents admissibles, couvrant des services comme les visites chez le médecin et les soins hospitaliers. Cependant, plusieurs services comme les médicaments d'ordonnance, les soins dentaires, les soins de la vue et les services paramédicaux ne sont pas entièrement couverts. L'assurance santé privée complète les régimes provinciaux en couvrant ces services additionnels. Les titulaires de police paient des primes mensuelles et reçoivent des prestations selon les détails de couverture de leur régime.",
    },
    {
      question: "Les régimes d'assurance maladie couvrent-ils les médicaments d'ordonnance?",
      answer:
        "Oui, la plupart des régimes d'assurance maladie privée au Canada incluent une couverture pour les médicaments d'ordonnance non entièrement payés par les régimes provinciaux. Cela garantit que vous et votre famille pouvez accéder aux médicaments nécessaires sans stress financier, améliorant la santé et le bien-être général. La couverture des médicaments aide à gérer les coûts de santé, protège contre les dépenses médicales imprévues et assure la continuité du traitement, faisant de l'assurance maladie privée un complément pratique aux soins de santé publics pour les Canadiens en quête de sécurité financière et médicale complète.",
    },
  ];

  return (
    <>
      <SEO
        title="Assurance maladie au Canada | Protégez votre santé – einsured.ca"
        description="Obtenez une assurance maladie au Canada avec einsured.ca. Comparez les régimes et choisissez une couverture adaptée à votre santé et à votre famille."
        keywords="assurance maladie Canada, assurance maladie privée, couverture médicale Canada"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[56vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start text-center md:text-left"
        style={{ backgroundImage: `url(${img30})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="text-white max-w-[28rem] sm:max-w-xl">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold leading-tight mb-3 sm:mb-4">
              Assurance maladie
            </h1>
            <p className="text-base sm:text-xl font-semibold mb-6 sm:mb-8">
              Des régimes complets pour les particuliers et les familles offrant des coûts prévisibles et une couverture fiable pour les besoins médicaux
            </p>
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full shadow-md transition"
            >
              Commencer
            </a>
          </div>
        </div>
      </section>

      {/* UNDERSTANDING HEALTH INSURANCE */}
      <section className="py-12 sm:py-16 bg-white text-gray-800">
        <div className={container}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-5 sm:mb-6">
              Comprendre l'assurance maladie au Canada
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center">
              Bienvenue dans notre guide complet sur la navigation dans le monde de l'{" "}
              <span className="text-sky-600 font-semibold">
                assurance maladie canadienne
              </span>
              . Notre mission en tant que courtier d'assurance est de vous aider à comprendre l'importance de l'assurance maladie, le fonctionnement du système de santé canadien et pourquoi disposer de la bonne couverture est essentiel pour vous et votre famille.
            </p>

            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mt-10 sm:mt-14 mb-5">
              Pourquoi l'assurance maladie est-elle importante au Canada ?
            </h3>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5 sm:mb-6 text-center">
              Le Canada est mondialement reconnu pour son système de soins de santé financé par l'État, qui offre une couverture pour un large éventail de services médicaux aux résidents canadiens. Cependant, il est essentiel de reconnaître que le système de soins de santé public ne couvre pas tout. Des éléments tels que les médicaments sur ordonnance hors des hôpitaux, les soins dentaires, les soins de la vue et les services paramédicaux peuvent n'être que partiellement couverts ou pas du tout, selon votre province ou territoire.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center">
              C'est là que l'{" "}
              <span className="text-sky-600 font-semibold">
                assurance maladie canadienne
              </span>{" "}
              devient indispensable. Avoir le bon régime d'assurance maladie peut combler ces lacunes, en vous garantissant, à vous et à vos proches, un accès à des services de soins de santé complets sans le stress des dépenses imprévues.
            </p>
          </div>
        </div>
      </section>

      {/* HOW THE CANADIAN HEALTHCARE SYSTEM WORKS */}
      <section className="py-12 sm:py-2 text-gray-800">
        <div className={container}>
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-5 sm:mb-6">
              Fonctionnement du système de santé canadien
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Le système de santé canadien est financé par l'État et administré sur une base provinciale ou territoriale, selon les lignes directrices établies par la Loi canadienne sur la santé. Bien que tous les Canadiens aient un accès raisonnable aux services de santé sans frais à payer pour les services médicaux essentiels, les services spécifiques couverts peuvent varier.
            </p>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-7 sm:mb-8">
              Comprendre la distinction entre ce qui est couvert et ce qui ne l'est pas peut parfois prêter à confusion, et c'est là que nous intervenons. Nous sommes là pour vous aider à naviguer dans ces nuances, en vous assurant de comprendre exactement ce que votre couverture actuelle comprend et où un régime d'assurance maladie privé pourrait vous être bénéfique.
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Obtenez votre régime maintenant"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-7 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-md transition"
            >
              Obtenez votre régime maintenant !
            </a>
          </div>
        </div>
      </section>

      {/* UNDERSTANDING HEALTHCARE COVERAGE*/}
      <section
        className="relative mt-10 py-14 sm:py-20 md:py-30 bg-cover bg-center bg-scroll md:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={`relative z-10 w-full ${container}`}>
          <div className="max-w-6xl mx-auto text-center text-white">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold leading-tight mb-2 sm:mb-3">
              Comprendre la couverture des soins de santé au Canada :
            </h2>
            <h3 className="text-lg sm:text-2xl md:text-3xl font-semibold text-sky-100 mb-6 sm:mb-8">
              Un guide sur les services provinciaux et les contributions gouvernementales
            </h3>
            <p className="max-w-5xl mx-auto text-base sm:text-lg leading-7 sm:leading-8 mb-8 sm:mb-10">
              Bienvenue dans notre guide complet sur la{" "}
              <a
                href="https://www.canada.ca/en/health-canada/services/health-cards.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-300 underline hover:text-sky-200"
              >
                couverture des soins de santé au Canada
              </a>
              , où nous explorons en détail la couverture provinciale et la mesure dans laquelle les cartes santé gouvernementales donnent accès aux services médicaux à travers le pays. Pilier de la société canadienne, le système de santé est conçu pour garantir à tous les citoyens et résidents permanents l'accès aux services médicaux nécessaires. Cependant, les spécificités peuvent varier selon la province et le territoire, ce qui rend essentiel de comprendre ce qui est couvert et où.
            </p>
            <h3 className="text-xl sm:text-3xl md:text-4xl font-bold mb-5 sm:mb-6">
              Les bases du système de santé canadien
            </h3>

            {/* Two Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 md:gap-8 max-w-6xl mx-auto mb-10 sm:mb-12">
              <div className="bg-white text-gray-800 rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-5 sm:p-8">
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  <a
                    href="https://www.canada.ca/en/health-canada/services/canada-health-care-system.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-600 underline"
                  >
                    Le système de santé canadien
                  </a>{" "}
                  est financé par l'État et administré sur une base provinciale ou territoriale, dans le cadre des lignes directrices établies par le gouvernement fédéral. Le système est principalement financé par la fiscalité.
                </p>
              </div>

              <div className="bg-white text-gray-800 rounded-2xl sm:rounded-3xl shadow-xl border border-white/20 p-5 sm:p-8">
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Chaque province et territoire offre son propre régime d'assurance maladie, et les résidents doivent demander une carte santé pour accéder aux services. La couverture peut varier considérablement d'une région à l'autre, mais certains services de base sont offerts partout.
                </p>
              </div>
            </div>

            <p className="text-base sm:text-lg leading-relaxed mb-5 sm:mb-6">
              Il est important que les personnes qui font une demande de{" "}
              <a
                href="https://www.canada.ca/en/immigration-refugees-citizenship/services/visit-canada/parent-grandparent-super-visa/about.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-300 underline hover:text-sky-200"
              >
                super visa
              </a>{" "}
              examinent attentivement les modalités et conditions de la police d'assurance, en s'assurant qu'elle répond aux exigences spécifiques établies par le gouvernement canadien. La police d'assurance doit être achetée auprès d'une{" "}
              <a href="/" className="text-sky-300 underline hover:text-sky-200">
                compagnie d'assurance canadienne
              </a>
              , et la couverture doit être maintenue pendant toute la durée du séjour sous super visa.
            </p>

            <p className="text-base sm:text-lg leading-relaxed mb-8 sm:mb-10">
              De plus, les demandeurs doivent obtenir les documents requis, y compris la lettre de confirmation de validité, avant de faire leur demande de super visa.
            </p>
            {/* CTA */}
            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Connaître le meilleur prix"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-7 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-md transition"
            >
              Connaître le meilleur prix
            </a>
          </div>
        </div>
      </section>

      {/* PROVINCIAL & TERRITORIAL HEALTH COVERAGE */}
      <section className="py-12 sm:py-20 bg-white text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-5 sm:mb-6">
              Couverture provinciale et territoriale des soins de santé
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center mb-7 sm:mb-8">
              Bien que les détails varient, il existe des catégories générales de services médicaux couverts par les régimes de santé provinciaux et territoriaux :
            </p>

            <ul className="space-y-5 sm:space-y-7 max-w-5xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">
                    Soins primaires :
                  </span>{" "}
                  Consultations chez les médecins de famille et les spécialistes auxquels ils réfèrent.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">
                    Soins hospitaliers :
                  </span>{" "}
                  Comprend les séjours, les chirurgies et les traitements d'urgence.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">
                    Services diagnostiques :
                  </span>{" "}
                  Analyses de laboratoire, radiographies et autres procédures diagnostiques prescrites par un professionnel de la santé.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT % OF MEDICAL SERVICES IS COVERED */}
      <section className="relative overflow-hidden bg-[#e9eff7]">
        <div className="absolute top-0 left-0 right-0 h-[3px] sm:h-1 bg-[#2b4672]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-[3px] sm:h-1 bg-[#2b4672]"></div>

        <div className={`relative z-10 py-12 sm:py-20 text-gray-800`}>
          <div className={container}>
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-7 sm:mb-8">
                Quel pourcentage des services médicaux est couvert ?
              </h2>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5 sm:mb-6">
                Les{" "}
                <span className="text-sky-600">cartes d'assurance maladie gouvernementales</span> au Canada couvrent généralement environ 70 % à 75 % des services médicaux selon les normes de santé publique. Cette couverture comprend la plupart des soins primaires, des services diagnostiques et des traitements hospitaliers. Cependant, certains domaines de soins, tels que les services dentaires, les médicaments sur ordonnance hors des hôpitaux, les soins de la vue et d'autres, peuvent ne pas être entièrement couverts ou pas du tout, selon la province.
              </p>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8 sm:mb-10">
                Il est conseillé aux Canadiens de souscrire une assurance maladie privée complémentaire pour couvrir ces services supplémentaires.
              </p>

              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#49628b] mb-3 sm:mb-4">
                Services non couverts
              </h3>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5 sm:mb-6">
                Bien que le système de santé canadien couvre un large éventail de services, il existe des exclusions notables dans les provinces et les territoires :
              </p>

              <ul className="space-y-4 sm:space-y-5 mb-7 sm:mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="text-base sm:text-lg leading-relaxed">
                    Médicaments sur ordonnance (hors des hôpitaux)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="text-base sm:text-lg leading-relaxed">
                    Soins dentaires (sauf pour des populations spécifiques)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="text-base sm:text-lg leading-relaxed">
                    Soins de la vue (examens de la vue et lunettes pour adultes)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle
                    aria-hidden="true"
                    className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                    strokeWidth={2.5}
                  />
                  <span className="text-base sm:text-lg leading-relaxed">
                    Appareils médicaux et certains traitements spécialisés
                  </span>
                </li>
              </ul>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5 sm:mb-6">
                De nombreux Canadiens s'appuient sur une assurance maladie complémentaire offerte par leur employeur ou des régimes privés pour combler ces lacunes.
              </p>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5 sm:mb-6">
                Le système de santé canadien offre une couverture par le biais de cartes d'assurance maladie gouvernementales, garantissant à tous les résidents l'accès aux services médicaux nécessaires. Bien que la couverture soit étendue, il existe des variations entre les provinces et les territoires, ainsi que des services non couverts, ce qui incite certains à rechercher une assurance complémentaire.
              </p>

              <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5 sm:mb-6">
                Comprendre les spécificités de votre couverture de santé provinciale ou territoriale est essentiel pour naviguer efficacement dans le système et savoir quelles couvertures supplémentaires pourraient être nécessaires pour des soins de santé complets.
              </p>

              <p className="text-base sm:text-lg leading-7 sm:leading-8">
                Pour obtenir les informations les plus récentes sur la couverture des soins de santé dans votre région,{" "}
                <a
                  href="https://book.einsured.ca/widget/bookings/ei-team"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-700 underline"
                >
                  contactez-nous dès maintenant.
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* THE ROLE OF PRIVATE HEALTH INSURANCE */}
      <section className="py-12 sm:py-20 bg-white text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6 sm:mb-8">
              Le rôle de l'assurance maladie privée
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-7 sm:mb-8">
              <span className="text-sky-600 font-semibold">
                L'assurance maladie privée au Canada
              </span>{" "}
              complète la couverture offerte par le système de soins de santé public. Elle est conçue pour offrir une tranquillité d'esprit supplémentaire pour les services non couverts ou partiellement couverts par votre régime d'assurance maladie provincial ou territorial. Ces services peuvent inclure :
            </p>

            <ul className="space-y-6 sm:space-y-7 max-w-5xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">
                    Médicaments sur ordonnance :
                  </span>{" "}
                  Bien que certaines provinces aient des programmes pour aider à couvrir les coûts des médicaments, ceux-ci ne couvrent pas tous les médicaments pour tous les résidents.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">Soins dentaires :</span>{" "}
                  Visites régulières chez le dentiste pour des bilans, des nettoyages, des obturations et plus encore, qui ne sont pas couverts par les soins de santé publics.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">Soins de la vue :</span>{" "}
                  Les examens de la vue et les lentilles correctrices peuvent représenter une dépense importante, généralement non couverte pour les adultes par les soins de santé publics.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 shrink-0"
                  strokeWidth={2.5}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  <span className="font-bold text-[#49628b]">
                    Services paramédicaux :
                  </span>{" "}
                  Les services fournis par les chiropraticiens, les physiothérapeutes et les massothérapeutes ne sont que partiellement couverts, voire pas du tout, par les régimes de santé provinciaux.
                </p>
              </li>
            </ul>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mt-7 sm:mt-8">
              Choisir le bon régime d'assurance maladie peut vous protéger contre ces dépenses et d'autres dépenses potentielles en matière de soins de santé, vous permettant, à vous et à votre famille, de vous concentrer sur ce qui compte vraiment : votre santé et votre bien-être.
            </p>
          </div>
        </div>
      </section>

      {/* FINDING THE RIGHT HEALTH INSURANCE PLAN (FULL SECTION WITH CONCLUSION) */}
      <section className="py-12 sm:py-2 bg-white text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-center">
            {/* Section Heading */}
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-5 sm:mb-6">
              Trouver le bon régime d'assurance maladie
            </h2>
            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8 sm:mb-10">
              Notre rôle en tant que votre courtier d'assurance de confiance est de vous guider tout au long du processus de sélection d'un régime d'assurance maladie qui répond à vos besoins.
            </p>

            {/* Subheading */}
            <h3 className="text-lg sm:text-2xl font-bold text-[#49628b] mb-8 sm:mb-10">
              Voici comment nous pouvons vous aider :
            </h3>

            {/* Cards Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 mb-14 sm:mb-16">
              {/* Card 1 */}
              <div className="bg-[#f2f5fa] rounded-2xl sm:rounded-3xl shadow-md p-6 sm:p-8 border border-[#dfe6f0] text-left">
                <h4 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Consultation personnalisée
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Nous commençons par une consultation personnalisée pour comprendre vos besoins spécifiques en matière de couverture santé. Que vous soyez travailleur autonome, entre deux emplois ou à la recherche d'une couverture supplémentaire au-delà de ce que propose votre employeur, nous sommes là pour trouver une solution adaptée à votre vie.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f2f5fa] rounded-2xl sm:rounded-3xl shadow-md p-6 sm:p-8 border border-[#dfe6f0] text-left">
                <h4 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Comparaison complète du marché
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Nous travaillons avec divers assureurs pour offrir une large gamme de produits d'assurance maladie. Notre équipe compare les régimes du marché entier, vous assurant d'obtenir la meilleure couverture aux tarifs les plus compétitifs.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f2f5fa] rounded-2xl sm:rounded-3xl shadow-md p-6 sm:p-8 border border-[#dfe6f0] text-left">
                <h4 className="text-lg sm:text-xl font-bold text-[#49628b] mb-3 sm:mb-4">
                  Soutien et conseils continus
                </h4>
                <p className="text-base sm:text-lg leading-7 sm:leading-8">
                  Choisir un régime n'est que le début. Notre engagement envers vous se poursuit, avec un soutien et des conseils continus pour vous aider à prendre des décisions éclairées concernant votre couverture d'assurance maladie à mesure que vos besoins évoluent avec le temps.
                </p>
              </div>
            </div>

            {/* Conclusion  */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-5 sm:mb-6">
              Conclusion
            </h3>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-7 sm:mb-8">
              Au Canada, bien que nous ayons la chance de disposer d'un solide système de soins de santé public, celui-ci ne couvre pas tous les besoins de santé.{" "}
              <span className="text-sky-600 font-semibold">
                L'assurance maladie canadienne
              </span>{" "}
              est un outil essentiel pour garantir à vous et à votre famille l'accès aux meilleurs soins possibles sans contrainte financière excessive. Notre équipe chez einsured.ca est dédiée à vous fournir les informations, le soutien et les conseils dont vous avez besoin pour faire les meilleurs choix pour votre couverture d'assurance maladie. Ensemble, nous pouvons assurer votre santé et votre tranquillité d'esprit pour l'avenir.
            </p>

            <h4 className="text-xl sm:text-3xl font-bold text-[#49628b] mb-10">
              Prêt à explorer vos options d'assurance maladie ?
            </h4>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold text-base sm:text-lg px-8 sm:px-10 py-3.5 sm:py-4 rounded-full shadow-md transition mb-8 sm:mb-10"
            >
              Contactez-nous aujourd'hui pour une consultation gratuite et sans engagement.
            </a>

            <p className="text-lg sm:text-2xl font-bold text-[#49628b] mb-8">
              Travaillons ensemble pour trouver la couverture d'assurance maladie qui vous convient, à vous et à votre famille.
            </p>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-white text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <div className="max-w-5xl mx-auto text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#49628b]">
              Foire aux questions sur l'assurance maladie
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => {
              const expanded = activeIndex === index;
              const panelId = `faq-panel-${index}`;
              const btnId = `faq-btn-${index}`;

              return (
                <div
                  key={index}
                  className="border rounded-lg shadow-sm transition-all bg-white"
                >
                  <button
                    id={btnId}
                    className="w-full flex justify-between items-center text-left px-4 sm:px-6 py-3.5 sm:py-4 font-semibold text-base sm:text-lg text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1e48a0] focus-visible:ring-offset-2"
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                  >
                    <span>{faq.question}</span>

                    {expanded ? (
                      <Minus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        className="w-6 h-6 text-gray-700 shrink-0 ml-4"
                        aria-hidden="true"
                      />
                    )}
                  </button>

                  {expanded && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={btnId}
                      className="px-4 sm:px-6 pb-4 text-gray-700 text-sm sm:text-base leading-relaxed"
                    >
                      {faq.answer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
